import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheckered, Terminal } from "@phosphor-icons/react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#05050A] text-white font-mono">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden border-r border-[#1F1F33] p-12">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div
          className="absolute inset-0 opacity-[0.12] bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.pexels.com/photos/14000469/pexels-photo-14000469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
        />
        <div className="relative flex items-center gap-3">
          <ShieldCheckered size={34} weight="duotone" color="#00F0FF" />
          <span className="font-head text-xl tracking-tighter font-bold">SENTINEL<span className="text-[#00F0FF]">/</span>SOC</span>
        </div>
        <div className="relative">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-4">Security Operations Center</p>
          <h1 className="font-head text-5xl leading-tight tracking-tighter font-black">
            REAL-TIME<br />THREAT<br /><span className="text-[#00F0FF]">DEFENSE</span>
          </h1>
          <p className="mt-6 text-sm text-[#A1A1AA] max-w-md leading-relaxed">
            Monitor threats, orchestrate incident response and analyze attack surface with AI-assisted intelligence — all in one command console.
          </p>
        </div>
        <div className="relative flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
          <span className="w-2 h-2 bg-[#FF3B30] pulse-dot" /> Live monitoring active
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="auth-form">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <ShieldCheckered size={28} weight="duotone" color="#00F0FF" />
            <span className="font-head text-lg font-bold tracking-tighter">SENTINEL/SOC</span>
          </div>

          <div className="flex items-center gap-2 mb-2 text-[#00F0FF]">
            <Terminal size={16} />
            <span className="text-[10px] uppercase tracking-[0.3em]">{mode === "login" ? "Operator Login" : "Register Operator"}</span>
          </div>
          <h2 className="font-head text-2xl tracking-tight font-bold mb-8">Access the console</h2>

          {mode === "register" && (
            <div className="mb-4">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Name</label>
              <input
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-[#0C0C14] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] transition-colors"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#0C0C14] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] transition-colors"
            />
          </div>

          <div className="mb-6">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#0C0C14] border border-[#1F1F33] rounded-none px-3 py-2.5 text-sm outline-none focus:border-[#00F0FF] transition-colors"
            />
          </div>

          {error && (
            <div data-testid="auth-error" className="mb-4 text-xs text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2">
              {error}
            </div>
          )}

          <button
            data-testid="auth-submit-button"
            type="submit"
            disabled={busy}
            className="w-full bg-[#00F0FF] text-black font-bold uppercase tracking-[0.2em] text-xs py-3 rounded-none hover:bg-white active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
          >
            {busy ? "Authenticating…" : mode === "login" ? "Sign In" : "Create Account"}
          </button>

          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="w-full mt-4 text-[11px] text-[#A1A1AA] hover:text-[#00F0FF] transition-colors"
          >
            {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
